import { useState, useRef, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  Loader2,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Minimize2,
  BookOpen,
  WifiOff,
  Eye,
  Terminal,
  Box,
  FolderOpen,
  Lock,
  AlertCircle,
} from 'lucide-react'
import { searchGuide, type GuideSearchResult } from '../../lib/api'
import { RichMarkdown } from '../content/RichMarkdown'
import { troubleshootingItems } from '../../lib/troubleshooting-data'
import type { GuideIndex, GuideContentEnhanced, GuideSectionMeta } from '../../lib/api'
import { ContentSkeleton } from '../ui/Skeleton'

const tsIconMap: Record<string, React.ElementType> = {
  'wifi-off': WifiOff,
  eye: Eye,
  terminal: Terminal,
  box: Box,
  'folder-open': FolderOpen,
  lock: Lock,
  'alert-circle': AlertCircle,
}

/** Fallback sections when index hasn't loaded yet */
const FALLBACK_SECTIONS = [
  { slug: 'getting-started', label: 'Getting Started' },
  { slug: 'workspaces', label: 'Workspaces' },
  { slug: 'apps', label: 'Apps' },
  { slug: 'settings', label: 'Settings' },
  { slug: 'feedback', label: 'Feedback' },
  { slug: 'analytics', label: 'Analytics' },
]

interface WidgetHelpTabProps {
  isExpanded: boolean
  guideIndex: GuideIndex | null
  currentSection: string
  setCurrentSection: (section: string) => void
  pendingAnchor: string | null
  setPendingAnchor: (anchor: string | null) => void
  sectionContent: GuideContentEnhanced | null
  loading: boolean
  indexLoading: boolean
  onClose: () => void
  onExpand: (section?: string, anchor?: string) => void
  onCollapse: () => void
  onReportBug?: () => void
}

function highlightText(text: string, term: string) {
  if (!term || term.length < 2) return text
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-primary/20 text-foreground rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  )
}

/** Sidebar section item with collapsible children */
function SidebarSection({
  section,
  currentSection,
  onSelect,
}: {
  section: GuideSectionMeta
  currentSection: string
  onSelect: (slug: string) => void
}) {
  const isActive = section.slug === currentSection
  const hasActiveChild = section.children.some((c) => c.slug === currentSection)
  const isOpen = isActive || hasActiveChild
  const hasChildren = section.children.length > 0

  return (
    <div>
      <button
        onClick={() => onSelect(section.slug)}
        className={`w-full text-left px-4 py-2 text-sm transition-colors min-h-[36px] flex items-center gap-1 ${
          isActive
            ? 'bg-primary/10 text-primary font-medium border-r-2 border-primary'
            : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
        }`}
      >
        {hasChildren && (
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </span>
        )}
        <span className={hasChildren ? '' : 'pl-5'}>{section.title}</span>
      </button>
      {isOpen && hasChildren && (
        <div className="ml-4 border-l border-border">
          {section.children.map((child) => (
            <button
              key={child.slug}
              onClick={() => onSelect(child.slug)}
              className={`w-full text-left pl-5 pr-4 py-1.5 text-xs transition-colors min-h-[30px] ${
                currentSection === child.slug
                  ? 'text-primary font-medium bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              {child.title}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function WidgetHelpTab({
  isExpanded,
  guideIndex,
  currentSection,
  setCurrentSection,
  pendingAnchor,
  setPendingAnchor,
  sectionContent,
  loading,
  onClose,
  onExpand,
  onCollapse,
  onReportBug,
}: WidgetHelpTabProps) {
  const [query, setQuery] = useState('')
  const [tsExpandedIds, setTsExpandedIds] = useState<Set<string>>(new Set())
  const [results, setResults] = useState<GuideSearchResult[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Scroll to pending anchor after content loads
  useEffect(() => {
    if (!pendingAnchor || loading || !sectionContent || !contentRef.current) return
    // Small delay to ensure DOM has rendered
    const timer = setTimeout(() => {
      const el = contentRef.current?.querySelector(`#${CSS.escape(pendingAnchor)}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      setPendingAnchor(null)
    }, 150)
    return () => clearTimeout(timer)
  }, [pendingAnchor, loading, sectionContent, setPendingAnchor])

  const handleInputChange = useCallback((value: string) => {
    setQuery(value)

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    if (value.length < 2) {
      setResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    setSearchLoading(true)

    timerRef.current = setTimeout(async () => {
      try {
        const guideResults = await searchGuide(value)
        setResults(guideResults)
      } catch {
        setResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 300)
  }, [])

  const handleResultClick = useCallback(
    (result: GuideSearchResult) => {
      setQuery('')
      setResults([])
      setIsSearching(false)
      onExpand(result.slug)
    },
    [onExpand]
  )

  const handleSectionClick = useCallback(
    (slug: string) => {
      if (isExpanded) {
        setCurrentSection(slug)
      } else {
        onExpand(slug)
      }
    },
    [isExpanded, setCurrentSection, onExpand]
  )

  // Use guide index sections if available, otherwise fallback
  const sections = guideIndex?.sections || null

  // ─── Expanded view: sidebar sections + content ───
  if (isExpanded) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Header bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border flex-shrink-0">
          <button
            onClick={onCollapse}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-foreground flex-1">User Guide</span>
          {currentSection && (
            <Link
              to={`/guide/${currentSection}`}
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
              aria-label="View full page"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          )}
          <button
            onClick={onCollapse}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Collapse"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Two-column layout: sidebar + content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar — section list with collapsible children */}
          <div className="w-48 flex-shrink-0 border-r border-border overflow-y-auto bg-secondary/20">
            <div className="py-2">
              {sections ? (
                sections.map((section) => (
                  <SidebarSection
                    key={section.slug}
                    section={section}
                    currentSection={currentSection}
                    onSelect={setCurrentSection}
                  />
                ))
              ) : (
                FALLBACK_SECTIONS.map((section) => (
                  <button
                    key={section.slug}
                    onClick={() => setCurrentSection(section.slug)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors min-h-[36px] ${
                      currentSection === section.slug
                        ? 'bg-primary/10 text-primary font-medium border-r-2 border-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                    }`}
                  >
                    {section.label}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-5" key={currentSection}>
              {loading ? (
                <ContentSkeleton />
              ) : sectionContent ? (
                <RichMarkdown content={sectionContent.content} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Select a section to view help content.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ─── Collapsed view: search + vertical section list ───
  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Search bar */}
      <div className="px-4 pt-4 pb-3 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Search help articles..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-secondary/50 border border-border rounded-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
          {searchLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border" />

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        {isSearching ? (
          /* Search results */
          <>
            {!searchLoading && results.length === 0 && (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-muted-foreground">No results found</p>
              </div>
            )}
            {results.map((result, i) => (
              <button
                key={`guide-${result.slug}-${i}`}
                onClick={() => handleResultClick(result)}
                className="w-full text-left px-5 py-3 hover:bg-secondary/50 transition-colors border-b border-border last:border-0 min-h-[44px]"
              >
                <span className="text-sm font-medium text-foreground">
                  {result.title}
                </span>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                  {highlightText(result.excerpt, query)}
                </p>
              </button>
            ))}
          </>
        ) : (
          /* Vertical section list with children + troubleshooting */
          <>
          <div className="py-1">
            {sections ? (
              sections.map((section) => (
                <div key={section.slug}>
                  <button
                    onClick={() => handleSectionClick(section.slug)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/50 transition-colors text-left min-h-[48px] border-b border-border"
                  >
                    <BookOpen className="w-4 h-4 text-primary flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">{section.title}</span>
                      {section.children.length > 0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {section.children.length + 1} articles
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                </div>
              ))
            ) : (
              FALLBACK_SECTIONS.map((section) => (
                <button
                  key={section.slug}
                  onClick={() => handleSectionClick(section.slug)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/50 transition-colors text-left min-h-[48px] border-b border-border last:border-0"
                >
                  <BookOpen className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground flex-1">{section.label}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </button>
              ))
            )}
          </div>

          {/* Troubleshooting section — same style as guide sections */}
          <div className="py-1 border-t border-border">
            <div className="px-5 py-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Troubleshooting</span>
            </div>
            {troubleshootingItems.map((item) => {
              const isExpTsItem = tsExpandedIds.has(item.id)
              const TsIcon = tsIconMap[item.icon] || AlertCircle
              return (
                <div key={item.id}>
                  <button
                    onClick={() =>
                      setTsExpandedIds((prev) => {
                        const next = new Set(prev)
                        if (next.has(item.id)) next.delete(item.id)
                        else next.add(item.id)
                        return next
                      })
                    }
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/50 transition-colors text-left min-h-[48px] border-b border-border"
                  >
                    <TsIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-foreground flex-1">{item.problem}</span>
                    {isExpTsItem ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </button>
                  {isExpTsItem && (
                    <div className="px-5 py-3 space-y-2 border-b border-border bg-secondary/20">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Why this happens</p>
                        <p className="text-sm text-muted-foreground">{item.cause}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">What to do</p>
                        <RichMarkdown content={item.fix} className="text-sm" />
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
            {onReportBug && (
              <div className="px-5 py-3">
                <p className="text-sm text-muted-foreground">
                  Can&apos;t find a solution?{' '}
                  <button onClick={onReportBug} className="text-primary font-medium hover:underline">
                    Submit a bug report
                  </button>
                </p>
              </div>
            )}
          </div>
          </>
        )}
      </div>
    </div>
  )
}
